import { useMemo } from 'react';

import { useFormState } from 'react-final-form';

import { Button, Skeleton, Typography, Box, Stack } from '@cast/design-system';
import { DateRange } from '@cast/shared-types';

import {
  useWorkloadLabelNamesQuery,
  useWorkloadLabelValuesQuery,
} from 'hooks/queries/cost';

import { LabelGroup } from '../../../_types';
import { LabelsFilterSchema } from '../../../validation.schema';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import {
  RemoveInputs,
  RffAutocompleteV2,
  RffInputsArrayRowState,
  RffMultiAutocompleteBadgeCount,
} from '@cast/shared-rff-ui';

type Props = {
  range: DateRange;
} & RffInputsArrayRowState<LabelGroup>;

export const LabelGroupInputs = ({ index, range, field, fields }: Props) => {
  const { values } = useFormState<LabelsFilterSchema>();
  const thisKey = fields.value[index].key;
  const selectedKeys = fields.value.map(({ key }) => key);
  const { cluster } = useCluster();
  const {
    isLoading: isLoadingLabelNames,
    labelNames,
    refetch,
    error,
  } = useWorkloadLabelNamesQuery({
    range,
    filter: {
      clusterIds: [cluster.id],
    },
  });

  const options = useMemo(() => {
    if (!labelNames) {
      return [];
    }
    const options = labelNames.filter((name) => !selectedKeys.includes(name));
    if (thisKey !== '') {
      options.push(thisKey);
    }
    return options;
  }, [labelNames, selectedKeys, thisKey]);

  const { isLoading: isLoadingLabelValues, labelValues } =
    useWorkloadLabelValuesQuery({
      label: values?.labels?.[index]?.key || '',
      range,
      filter: {
        clusterIds: [cluster.id],
      },
      enabled: !!values?.labels?.[index].key,
    });

  if (error) {
    return (
      <Stack direction="row" alignItems="center" gap={4}>
        <Typography variant="P12R" color="red.500">
          Failed to load available labels
        </Typography>
        <Button variant="text" onClick={refetch}>
          Reload
        </Button>
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap={8}>
      {isLoadingLabelNames ? (
        <Skeleton height={32} width={152} />
      ) : (
        <Box flex={1}>
          <RffAutocompleteV2
            name={`${field}.key`}
            items={options}
            placeholder="Select key"
            searchInputPlaceholder="Search label keys"
            testId="key"
          />
        </Box>
      )}
      {isLoadingLabelValues ? (
        <Skeleton height={32} width={152} />
      ) : (
        <Box sx={{ minWidth: 152, width: 152 }}>
          <RffMultiAutocompleteBadgeCount
            name={`${field}.value`}
            items={labelValues || []}
            searchInputPlaceholder="Search values"
            placeholder="Select value"
            disabled={!thisKey || !labelValues?.length}
            testId="value"
            showSelectAllItem
            indicatorLabel="Selected"
            suffixOutside={<RemoveInputs />}
          />
        </Box>
      )}
    </Stack>
  );
};
